import { ArrowUpRight, TrendingDown, Wallet } from "lucide-react";
import { useId } from "react";
import { Link } from "react-router-dom";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import type { OtcVolumeSuggestion } from "../../types/cryptoSwaps.types";
import type { PairPrecision } from "../../utils/cryptoSwapFormat";
import {
  addsUnits,
  meterPercent,
  NEAR_BEST_PERCENT,
  perUnitPrecision,
  savesPerUnit,
  showsPerUnit,
  type VolumeDiscountView,
} from "../../utils/volumeDiscount";

const WHOLE = { min: 0, max: 0 };
const UNITS = { min: 2, max: 2 };
const WALLETS_PATH = "/app/wallets";

export interface VolumeDiscountBannerProps {
  view: VolumeDiscountView;
  pair: PairPrecision;
  onApply: (suggestion: OtcVolumeSuggestion) => void;
}

export function VolumeDiscountBanner({ view, pair, onApply }: VolumeDiscountBannerProps) {
  const { t } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const titleId = useId();
  const { insight, stale } = view;
  const selling = insight.side === "SELL";
  const currency = insight.currency || (selling ? pair.receive_currency : pair.send_currency);
  const asset = insight.asset || (selling ? pair.send_currency : pair.receive_currency);
  const units = (value: string) => `${format(value, asset, UNITS)} ${asset}`;
  const percent = meterPercent(insight);
  const money = (value: string) => `${format(value, currency, WHOLE)} ${currency}`;
  const perUnit = (value: string) => `${format(value, currency, perUnitPrecision(value))} ${currency}`;
  const savedPerUnit =
    insight.discount && showsPerUnit(insight.discount.per_unit) ? perUnit(insight.discount.per_unit) : null;

  const meterCaption =
    percent === null
      ? null
      : percent >= NEAR_BEST_PERCENT
        ? t("volume_meter_best")
        : percent > 0 && savedPerUnit
          ? t(selling ? "volume_sell_meter_saving" : "volume_meter_saving", { amount: savedPerUnit })
          : t(selling ? "volume_sell_meter_minimum" : "volume_meter_minimum");

  return (
    <section
      aria-labelledby={titleId}
      aria-busy={stale}
      className={cn("flex flex-col gap-4 transition-opacity duration-200", stale && "opacity-60")}
    >
      {insight.insufficient_balance ? (
        <ColocaAlert
          id={titleId}
          variant="warning"
          icon={Wallet}
          heading={t("volume_insufficient_title")}
          text={
            savedPerUnit
              ? t("volume_insufficient_text", {
                  amount: money(insight.send_amount),
                  savings: savedPerUnit,
                })
              : undefined
          }
        />
      ) : (
        <h2
          id={titleId}
          className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground"
        >
          <TrendingDown aria-hidden className="size-5 text-success" />
          {t(selling ? "volume_sell_title" : "volume_title")}
        </h2>
      )}

      {percent !== null && (
        <div className="flex flex-col gap-2">
          <ColocaProgress value={percent} label={t("volume_meter_label")} showValue tone="success" />
          {meterCaption && <p className="text-sm text-muted-foreground">{meterCaption}</p>}
        </div>
      )}

      {insight.suggestions.length > 0 && (
        <ul className="flex flex-col gap-2">
          {insight.suggestions.map((suggestion) => (
            <li key={`${suggestion.kind}:${suggestion.send_amount}`}>
              <SuggestionRow
                suggestion={suggestion}
                insufficient={insight.insufficient_balance}
                selling={selling}
                money={money}
                perUnit={perUnit}
                units={units}
                onApply={onApply}
              />
            </li>
          ))}
        </ul>
      )}

      {insight.top_up && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <span>
            {t("volume_top_up", {
              amount: money(insight.top_up.send_amount),
              price: money(insight.top_up.price),
            })}
          </span>
          <Link
            to={WALLETS_PATH}
            className="inline-flex items-center gap-0.5 font-medium text-foreground underline-offset-4 hover:underline"
          >
            {t("volume_top_up_action")}
            <ArrowUpRight aria-hidden className="size-3.5" />
          </Link>
        </p>
      )}
    </section>
  );
}

interface SuggestionRowProps {
  suggestion: OtcVolumeSuggestion;
  insufficient: boolean;
  selling: boolean;
  money: (value: string) => string;
  perUnit: (value: string) => string;
  units: (value: string) => string;
  onApply: (suggestion: OtcVolumeSuggestion) => void;
}

function SuggestionRow({
  suggestion,
  insufficient,
  selling,
  money,
  perUnit,
  units,
  onApply,
}: SuggestionRowProps) {
  const { t } = useTranslations("cryptoSwaps");
  const ownBalance = suggestion.kind === "WALLET_BALANCE" || suggestion.kind === "MAX_AMOUNT";
  const amount = selling ? units(suggestion.send_amount) : money(suggestion.send_amount);
  const extra = selling ? money(suggestion.extra_receive) : units(suggestion.extra_receive);
  const title = selling
    ? t("volume_sell_suggestion_amount", { amount })
    : suggestion.kind === "MAX_AMOUNT"
      ? t("volume_suggestion_max", { amount })
      : ownBalance
        ? t(insufficient ? "volume_insufficient_wallet" : "volume_suggestion_wallet", { amount })
        : t("volume_suggestion_amount", { amount });
  const saves = savesPerUnit(suggestion);

  return (
    <button
      type="button"
      onClick={() => onApply(suggestion)}
      className={cn(
        colocaSurfaceVariants({ variant: "plain", padding: "sm", interactive: true }),
        "flex w-full items-center justify-between gap-3 rounded-xl",
        ownBalance && !insufficient && "border-success/50 bg-success-container/40",
      )}
    >
      <span className="flex min-w-0 items-start gap-2.5">
        {ownBalance && <Wallet aria-hidden className="mt-0.5 size-4 shrink-0 text-success" />}
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">
            {saves
              ? t(selling ? "volume_sell_suggestion_price" : "volume_suggestion_price", {
                  price: money(suggestion.price),
                  savings: perUnit(suggestion.savings_per_unit),
                })
              : t("volume_suggestion_price_only", { price: money(suggestion.price) })}
          </span>
        </span>
      </span>
      {saves && addsUnits(suggestion) && (
        <ColocaBadge variant="success" className="shrink-0">
          {t("volume_suggestion_extra", { amount: extra })}
        </ColocaBadge>
      )}
    </button>
  );
}
